import '../../../styles/pages.css'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  capitalize,
} from '@mui/material'
import {
  DistrictSchool,
  ILMSDistrictInfo,
  ISyncLimits,
  ISyncLimitUpdate,
  IUserCount,
  List,
} from 'infrastructure/api'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { getAPIRequest } from 'infrastructure/api'

import AddCurriculum from '../shared/AddCurriculum'
import AddSchoolToDistrict from './AddSchoolToDistrict'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { GetDistrictCurricula } from '../shared/GetCurricula'
import { GetUserRoleCount } from 'shared/api/organizations/organizations.utils'
import MigrateCurriculaToDistrict from './MigrateCurriculaToDistrict'
import { BulkRemoveCurriculum } from './BulkRemoveCurriculum'
import PurgeDistrictSISDataButton from './PurgeDistrictSISDataButton'
import { SchoolCreateWithDistrict } from '../school_components/SchoolCreate'
import SyncSISButton from '../shared/SyncSISButton'
import { apiUrl } from 'infrastructure/api'
import WarningIcon from '@mui/icons-material/Warning'
import { BulkAssignCurriculum } from './BulkAssignCurriculum'
import DistrictHeader from './DistrictHeader'
import { DistrictNotes } from './DistrictNotes'
import {
  useDistrictIngestionResource,
  useDistrictResource,
} from 'shared/api/district'
import { useResourceNavigate } from 'shared/api/resourceRouting'
import { Table } from 'shared/ui/Table/Table'
import { districtSchoolsAtomWithRefresh } from 'shared/api/district'
import { useAtom } from 'infrastructure/state/jotai.wrapper'
import ArchiveClassButton from '../shared/ArchiveClassButton'
import { getLMSDistrictInfo } from 'shared/api/mutations'
import LMSSync from './LMSSync'
import { PlainLink } from 'app/components/RouteLink'
import ReinstateResourceButton from '../shared/ReinstateResourceButton'
import DeleteOrgUsersButton from '../shared/DeleteOrgUsersButton'
import { CSVUpload } from 'shared/ui'

export function DistrictShow({ id }: { id: string }) {
  const {
    data: districtData,
    updateSyncSettings,
    reFetchDistrict,
    updateSyncLimitsSettings,
  } = useDistrictResource(id)
  const samlSPLogin = `${apiUrl()}/v1/saml/login?district_id=${id}`
  const acsURL = `${apiUrl()}/v1/saml/acs?district_id=${id}`
  const metadataURL = `${apiUrl()}/v1/saml/metadata?district_id=${id}`
  const [currentDialog] = useState<ReactElement | null>()
  const { show: showSchool } = useResourceNavigate('schools')
  const [lmsDistrictInfo, setLMSDistrictInfo] =
    useState<ILMSDistrictInfo | null>(null)

  const { data: ingestedDistrict, reFetchIngestion: getIngestedDistrict } =
    useDistrictIngestionResource(id)

  const [hasSAML, setHasSAML] = useState(false)

  const [userCount, setUserCount] = useState<IUserCount>({
    teacher_count: 0,
    admin_count: 0,
    student_count: 0,
  })

  const [districtSISID, setDistrictSISID] = useState('')
  const [automatedOffboarding, setAutomatedOffboarding] = useState(false)
  const [isDailySyncEnabled, setIsDailySyncEnabled] = useState(false)
  const [syncLimits, setSyncLimits] = useState<ISyncLimits>({
    sync_classes: true,
    sync_users: true,
    archive_classes: false,
    offboard_users: false,
    automated_offboarding: false,
  })

  const archiveButtonLabel = 'Archive All Classes in District'

  const checkSAML = async (id: any) => {
    try {
      const res = await getAPIRequest<undefined | { error?: unknown }>(
        `/v1/sis/ingestion/saml?district_id=${id}`,
      )
      if (res && res.error == null) {
        setHasSAML(true)
      }
    } catch {}
  }

  const getUserCount = useCallback(async (id: string) => {
    setUserCount(await GetUserRoleCount(id, 'district'))
  }, [])

  const getLMSInfo = useCallback(async (districtID: number) => {
    setLMSDistrictInfo(await getLMSDistrictInfo(districtID))
  }, [])

  useEffect(() => {
    districtData?.id && checkSAML(districtData.id)
    districtData?.id && getUserCount(districtData.id)
    // fetch district sis id and sis type
    districtData?.id && getIngestedDistrict()
    districtData?.id && getLMSInfo(Number(districtData.id))
  }, [getIngestedDistrict, getUserCount, districtData?.id])

  useEffect(() => {
    if (ingestedDistrict !== undefined) {
      setDistrictSISID(ingestedDistrict.district_sis_id)
      setIsDailySyncEnabled(ingestedDistrict.is_daily_sync_enabled)
      const limits = ingestedDistrict.sync_limits[ingestedDistrict.sis_type]
      if (limits !== undefined) {
        setSyncLimits(limits)
      }
    }
  }, [ingestedDistrict])

  useEffect(() => {
    if (syncLimits !== undefined) {
      setAutomatedOffboarding(syncLimits.automated_offboarding)
    }
  }, [syncLimits])

  const [districtSchools, setDistrictSchools] = useState<DistrictSchool[] | []>(
    [],
  )

  const ToggleNightlySync = async () => {
    if (!districtData) return
    const body = {
      enable_daily_sync: !isDailySyncEnabled,
    }
    try {
      await updateSyncSettings(body)
      // once successful, refetch ingested district
      getIngestedDistrict()
    } catch {
      // unable to update nightly sync status
    }
  }

  const ToggleAutomatedOffboarding = async () => {
    if (!ingestedDistrict) return
    const limits = syncLimits
    limits.automated_offboarding = !limits.automated_offboarding

    const body: ISyncLimitUpdate = {
      district_id: ingestedDistrict.district_id,
      sis_type: ingestedDistrict.sis_type,
      ingestion_id: ingestedDistrict.ingestion_id,
      sync_limits: limits,
      district_sis_id: ingestedDistrict.district_sis_id,
    }
    try {
      await updateSyncLimitsSettings(body)
      setSyncLimits(limits)
      setAutomatedOffboarding(syncLimits.automated_offboarding)
    } catch (e) {
      alert(e)
    }
  }

  const [Objectschools, refetch] = useAtom(districtSchoolsAtomWithRefresh)

  useEffect(() => {
    if (Object.keys(Objectschools).length !== 0 && districtData) {
      // Get district Id and school IDs saved in lcoal Storage
      const lastDistrictID = localStorage.getItem('lastDistrictID')
      const lastSchoolIDs = JSON.parse(
        localStorage.getItem('lastSchoolIDs') || '[]',
      )

      // Schools from server
      const schools: List.DistrictSchool[] = Object.values(Objectschools)
      const serverSchoolsIds = schools.map((school) => school.id)

      // Set schools adding a labelNew property if school is not in local sorage
      setDistrictSchools(
        schools?.map((school) => ({
          id: school.id,
          name: school.name,
          admin_count: school.admin_count,
          teacher_count: school.teacher_count,
          student_count: school.student_count,
          labelNew:
            lastDistrictID === String(districtData.id) &&
            !lastSchoolIDs?.includes(school.id),
        })),
      )

      // Saved new distrcit ID and school IDs in local storage
      localStorage.setItem('lastSchoolIDs', JSON.stringify(serverSchoolsIds))
      localStorage.setItem('lastDistrictID', String(districtData.id))
    }
  }, [districtData, Objectschools])

  const [hasCurriculumToMigrate, setHasCurriculumToMigrate] =
    useState<boolean>(false)

  const schoolColumns = useSchoolColumns()
  if (!districtData) {
    return <div>Loading...</div>
  }

  return (
    <div className="content-color">
      {currentDialog}
      <DistrictHeader
        district={districtData}
        ingestedDistrict={ingestedDistrict}
        onDistrictUpdated={reFetchDistrict}
      />
      <DistrictNotes districtId={id} initialNotes={districtData?.notes ?? ''} />
      {hasSAML && (
        <>
          <p>
            SAML Login (Service Provider Initiated): <b>{samlSPLogin}</b>
          </p>
          <p>
            SAML Assertion Endpoint (IDP Initiated Login): <b>{acsURL}</b>
          </p>
          <p>
            SAML Metadata Endpoint: <b>{metadataURL}</b>
          </p>
        </>
      )}
      {districtSISID && (
        <>
          <hr />
          <p>
            Daily Sync:
            <span
              className={isDailySyncEnabled ? 'sync-enable' : 'sync-disable'}
            >
              {` ${capitalize(String(isDailySyncEnabled))} `}
            </span>
            <span className="section-buttons">
              <Button
                variant="outlined"
                color="secondary"
                onClick={ToggleNightlySync}
                size="small"
              >
                Toggle
              </Button>
            </span>
          </p>
          <p>
            Automated offboarding:
            <span
              className={automatedOffboarding ? 'sync-enable' : 'sync-disable'}
            >
              {` ${capitalize(String(automatedOffboarding))} `}
            </span>
            <span className="section-buttons">
              <Button
                variant="outlined"
                color="secondary"
                onClick={ToggleAutomatedOffboarding}
                size="small"
              >
                Toggle
              </Button>
            </span>
          </p>
        </>
      )}
      <hr />
      <div>
        {districtSISID && (
          <>
            <SyncSISButton
              name={districtData.name}
              districtID={Number(districtData.id)}
              districtSISID={districtSISID}
              automatedOffboarding={automatedOffboarding}
            />
            <PurgeDistrictSISDataButton
              districtName={districtData.name}
              districtID={Number(districtData.id)}
            />
          </>
        )}
        <div className="section-buttons">
          <CSVUpload />
          <Button
            variant="outlined"
            component={PlainLink}
            to={`/districts/${districtData.id}/edit`}
          >
            Edit District
          </Button>
          {districtData.deleted_at && (
            <ReinstateResourceButton
              name={districtData.name}
              resource="District"
              districtID={parseInt(districtData.id)}
            />
          )}
        </div>
        <hr />
        {lmsDistrictInfo && <LMSSync districtLMSInfo={lmsDistrictInfo} />}
        <hr />
        <div className="section-buttons">
          <Button
            variant="outlined"
            component={PlainLink}
            to={`/users?filter.district=${districtData.id}`}
            style={{ marginRight: '10px' }}
          >
            View District Users
          </Button>
          <span>
            Total Admins: {userCount.admin_count} <b>| </b>
          </span>
          <span>
            Total Teachers: {userCount.teacher_count} <b>| </b>
          </span>
          <span style={{ marginRight: '10px' }}>
            Total Students: {userCount.student_count}
          </span>
          <DeleteOrgUsersButton
            districtID={Number(districtData.id)}
            name={districtData.name}
            resource="district"
          />
        </div>
        <hr />
        <div className="section-buttons">
          <ArchiveClassButton
            id={Number(districtData.id)}
            level={'district'}
            archive={true}
            label={archiveButtonLabel}
          />
        </div>
      </div>
      <hr />
      <Box
        sx={{ display: 'flex' }}
        width={'100%'}
        style={{ overflowY: 'auto', maxHeight: '300px' }}
      >
        <GetDistrictCurricula
          districtId={parseInt(districtData.id)}
          onDeleted={reFetchDistrict}
        />
      </Box>
      <hr />
      {hasCurriculumToMigrate && (
        <div className="section-warning">
          <WarningIcon />
          There are bundled curriculums in the school which should be migrated
          to district level
        </div>
      )}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Curricula
        </AccordionSummary>
        <AccordionDetails>
          <Box width={'100%'}>
            <Box sx={{ display: 'flex', marginTop: '1%' }}>
              <AddCurriculum
                districtID={parseInt(districtData.id)}
                onAdded={reFetchDistrict}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Bulk Assign Curricula to Schools
        </AccordionSummary>
        <AccordionDetails>
          <BulkAssignCurriculum
            districtSchools={districtSchools}
            onComplete={reFetchDistrict}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Bulk Remove Curricula from Schools
        </AccordionSummary>
        <AccordionDetails>
          <BulkRemoveCurriculum
            districtId={parseInt(districtData.id)}
            districtSchools={districtSchools}
            onComplete={reFetchDistrict}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Create new school for this district
        </AccordionSummary>
        <AccordionDetails>
          <SchoolCreateWithDistrict districtID={parseInt(districtData.id)} />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Add existing school to this district
        </AccordionSummary>
        <AccordionDetails>
          <AddSchoolToDistrict
            data={districtData}
            refetchDistrictSchools={refetch}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Migrate School Curricula to District
        </AccordionSummary>
        <AccordionDetails>
          <MigrateCurriculaToDistrict
            district={districtData}
            totalSchoolCount={districtSchools.length}
            setHasCurriculumToMigrate={setHasCurriculumToMigrate}
          />
        </AccordionDetails>
      </Accordion>
      <hr />
      <div>
        {!!districtSchools.length && (
          <Table
            columns={schoolColumns}
            items={districtSchools}
            onRowClick={(index: number) => {
              const { id } = districtSchools[index]
              showSchool(id)
            }}
            overrideHeight={(districtSchools.length + 1) * 33}
          />
        )}
      </div>
    </div>
  )
}

export function useSchoolColumns() {
  return useMemo(() => {
    return [
      {
        title: <strong>School Name</strong>,
        width: 200,
        rowContent({ name }: DistrictSchool) {
          return name
        },
      },
      {
        title: <strong>School Id</strong>,
        width: 50,
        rowContent({ id }: DistrictSchool) {
          return id
        },
      },
      {
        title: <strong>Admin Count</strong>,
        width: 200,
        rowContent({ admin_count }: DistrictSchool) {
          return admin_count
        },
      },
      {
        title: <strong>Teacher Count</strong>,
        width: 200,
        rowContent({ teacher_count }: DistrictSchool) {
          return teacher_count
        },
      },
      {
        title: <strong>Student Count</strong>,
        width: 200,
        rowContent({ student_count }: DistrictSchool) {
          return student_count
        },
      },
    ]
  }, [])
}
